import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Message, DetailedLesson, Flashcard } from '../../types';
import { api } from '../../services/api';
import { downloadAssistantContext } from '../../utils/assistantContext';
import { parseUtcDateTime } from '../../utils/time';
import { useRealtimeDictation } from '../../hooks/useRealtimeDictation';
import MarkdownContent from '../shared/MarkdownContent';

interface ChatInterfaceProps {
  messages: Message[];
  onSendMessage: (message: string) => void;
  connectionStatus: 'connecting' | 'connected' | 'disconnected';
  currentLesson: DetailedLesson | null;
  chatId: string;
  isAdmin?: boolean;
  completedSections: Record<string, 'LEARNED' | 'SKIPPED' | 'COMPLETED'>;
  showSidebar: boolean;
  onHideSidebar: () => void;
  currentSectionName: string;
  onSectionSelect: (sectionName: string) => void;
  onLessonCompletionClick?: () => void;
}

type UiMessage = {
  id: string;
  type: 'user' | 'tutor' | 'system' | 'section_completion' | 'lesson_completion' | 'flashcards_unlocked';
  content: string;
  timestamp: Date;
  completionData?: {
    status: 'LEARNED' | 'SKIPPED' | 'COMPLETED';
    sectionName?: string;
    lessonTitle?: string;
  };
  flashcards?: Flashcard[];
  modelContextAvailable?: boolean;
};

const isPhoneLikeInputDevice = () => {
  if (typeof window === 'undefined') return false;

  const hasCoarsePointer = window.matchMedia('(pointer: coarse)').matches;
  const hasSmallViewport = window.matchMedia('(max-width: 767px)').matches;
  const hasPhoneUserAgent = /Android|iPhone|iPod/i.test(window.navigator.userAgent);

  return hasCoarsePointer && (hasSmallViewport || hasPhoneUserAgent);
};

const isTutorMessage = (message: Message) =>
  message.type === 'tutor_response'
  || message.type === 'lesson_content'
  || message.type === 'lesson_started';

const DICTATION_PROMPT_PREFIX =
  'The speaker is replying to this tutoring assistant message:\n';
const MAX_DICTATION_PROMPT_LENGTH = 1000;

const ChatInterface: React.FC<ChatInterfaceProps> = ({
  messages,
  onSendMessage,
  connectionStatus,
  currentLesson,
  chatId,
  isAdmin = false,
  completedSections,
  showSidebar,
  onHideSidebar,
  currentSectionName,
  onSectionSelect,
  onLessonCompletionClick
}) => {
  const navigate = useNavigate();
  const [isPhoneInput, setIsPhoneInput] = useState(isPhoneLikeInputDevice);
  const [inputMessage, setInputMessage] = useState('');
  const [sendOnEnter, setSendOnEnter] = useState(() => {
    if (typeof window === 'undefined') return true;
    if (isPhoneLikeInputDevice()) return false;
    const stored = window.localStorage.getItem('tmn:sendOnEnter');
    if (stored === null) return true;
    return stored === 'true';
  });
  const [showConfig, setShowConfig] = useState(false);
  const [openFlashcardMessageId, setOpenFlashcardMessageId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messageRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const configRef = useRef<HTMLDivElement>(null);
  const lastAssistantMessage = [...messages]
    .reverse()
    .find(isTutorMessage)
    ?.content;
  const dictationPrompt = lastAssistantMessage
    ? DICTATION_PROMPT_PREFIX + lastAssistantMessage.slice(
      -(MAX_DICTATION_PROMPT_LENGTH - DICTATION_PROMPT_PREFIX.length),
    )
    : undefined;
  const dictation = useRealtimeDictation({
    prompt: dictationPrompt,
    onTranscript: setInputMessage,
  });
  const isDictating = dictation.isActive;
  const stopDictation = dictation.stop;

  const scrollToBottom = () => {
    const sentinel = messagesEndRef.current;
    const lastMessageEl = sentinel?.previousElementSibling as HTMLElement | null;
    if (lastMessageEl) {
      lastMessageEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const scrollToMessage = (messageId: string) => {
    const node = messageRefs.current[messageId];
    if (node) {
      node.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (connectionStatus === 'connected' && inputRef.current) {
      inputRef.current.focus();
    }
  }, [connectionStatus]);

  useEffect(() => {
    const textarea = inputRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
  }, [inputMessage]);

  useEffect(() => {
    if (connectionStatus !== 'connected' && isDictating) {
      stopDictation();
    }
  }, [connectionStatus, isDictating, stopDictation]);

  useEffect(() => {
    const updateInputDevice = () => setIsPhoneInput(isPhoneLikeInputDevice());
    window.addEventListener('resize', updateInputDevice);
    window.addEventListener('orientationchange', updateInputDevice);
    return () => {
      window.removeEventListener('resize', updateInputDevice);
      window.removeEventListener('orientationchange', updateInputDevice);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('tmn:sendOnEnter', String(sendOnEnter));
  }, [sendOnEnter]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!configRef.current) return;
      if (!configRef.current.contains(event.target as Node)) {
        setShowConfig(false);
      }
    };
    if (showConfig) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showConfig]);

  const performSend = () => {
    if (inputMessage.trim() && connectionStatus === 'connected') {
      dictation.stop();
      onSendMessage(inputMessage.trim());
      setInputMessage('');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    performSend();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const wantsSend = (!isPhoneInput && sendOnEnter && e.key === 'Enter' && !e.shiftKey)
      || (!sendOnEnter && e.key === 'Enter' && (e.metaKey || e.ctrlKey));
    if (wantsSend) {
      e.preventDefault();
      performSend();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputMessage(e.target.value);
  };

  const formatTimestamp = (timestamp: Date) => {
    return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const renderMathContent = (content: string) => {
    return <MarkdownContent content={content} />;
  };

  const normalizeCompletionData = (completionData?: Message['completionData']) => {
    if (!completionData) return undefined;
    const raw = completionData as any;
    return {
      status: raw.status,
      sectionName: raw.sectionName || raw.section_name,
      lessonTitle: raw.lessonTitle || raw.lesson_title
    };
  };

  const REVISION_COMPLIMENTS = [
    'Great work!',
    'Well done!',
    'Excellent!',
    'Nicely done!',
    'Impressive!',
    'You nailed it!',
    'Fantastic!',
    'Keep it up!',
    'Brilliant!',
    'Way to go!',
  ];

  const toUiMessage = (data: Message, index: number): UiMessage => {
    let type: UiMessage['type'] = 'system';
    if (data.type === 'tutor_response' || data.type === 'lesson_content' || data.type === 'lesson_started') {
      type = 'tutor';
    } else if (data.type === 'section_completion') {
      type = 'section_completion';
    } else if (data.type === 'flashcards_unlocked') {
      type = 'flashcards_unlocked';
    } else if (data.type === 'lesson_completion') {
      type = 'lesson_completion';
    } else if (data.type === 'user_message') {
      type = 'user';
    }

    const ts = data.timestamp ? parseUtcDateTime(data.timestamp) : new Date();
    let content = data.content || '';
    if (data.type === 'section_switch') {
      content = `Jumping to ${data.content || ''}`.trim();
    }
    if (data.type === 'section_completion') {
      if (currentLesson?.is_revision) {
        content = REVISION_COMPLIMENTS[index % REVISION_COMPLIMENTS.length];
      } else {
        content = `Learned ${data.content || ''}`.trim();
      }
    }
    if (data.type === 'flashcards_unlocked') {
      if (/\bunlocked\b/i.test(content)) {
        const count = data.flashcards?.length || 0;
        content = count > 0
          ? `Added ${count} flashcard${count === 1 ? '' : 's'} to your deck`
          : 'Flashcards added to your deck';
      }
    }

    return {
      id: `${data.type}-${index}-${data.timestamp || 'now'}`,
      type,
      content,
      timestamp: ts,
      completionData: normalizeCompletionData(data.completionData),
      flashcards: data.flashcards,
      modelContextAvailable: data.modelContextAvailable,
    };
  };

  const uiMessages = messages.map((message, index) => toUiMessage(message, index));

  const toggleFlashcards = (messageId: string) => {
    setOpenFlashcardMessageId(prev => (prev === messageId ? null : messageId));
  };

  const handleExportContext = async (assistantIndex: number) => {
    try {
      await downloadAssistantContext(chatId, assistantIndex, currentLesson?.id);
    } catch (e: any) {
      alert(e?.message || 'Failed to download context');
    }
  };

  const handleOpenPromptTester = async (assistantIndex: number) => {
    try {
      const context = await api.getAssistantContext(chatId, assistantIndex);
      navigate('/admin/prompt-tester', { state: { context } });
    } catch (e: any) {
      alert(e?.message || 'Failed to open context in Prompt Tester');
    }
  };

  const findLatestSectionStartIndex = (sectionName: string) => {
    if (!currentLesson) return -1;
    const sectionIndex = currentLesson.sections.findIndex(section => section.name === sectionName);
    if (sectionIndex === -1) return -1;
    const previousSectionName = sectionIndex > 0 ? currentLesson.sections[sectionIndex - 1].name : '';
    const candidates: number[] = [];

    messages.forEach((message, index) => {
      if (message.type === 'section_switch' && message.content === sectionName) {
        const nextTutorIndex = messages.slice(index + 1).findIndex(m => isTutorMessage(m));
        if (nextTutorIndex !== -1) {
          candidates.push(index + 1 + nextTutorIndex);
        }
      }
      if (previousSectionName && message.type === 'section_completion' && message.content === previousSectionName) {
        const nextTutorIndex = messages.slice(index + 1).findIndex(m => isTutorMessage(m));
        if (nextTutorIndex !== -1) {
          candidates.push(index + 1 + nextTutorIndex);
        }
      }
    });

    if (sectionIndex === 0) {
      const firstTutorIndex = messages.findIndex(message => isTutorMessage(message));
      if (firstTutorIndex !== -1) {
        candidates.push(firstTutorIndex);
      }
    }

    return candidates.length ? Math.max(...candidates) : -1;
  };

  const handleSectionClick = (sectionName: string) => {
    const startIndex = findLatestSectionStartIndex(sectionName);
    if (startIndex !== -1) {
      const targetMessage = uiMessages[startIndex];
      if (targetMessage) {
        scrollToMessage(targetMessage.id);
        return;
      }
    }
    onSectionSelect(sectionName);
  };

  const currentSectionIndex = currentLesson?.sections?.length
    ? (() => {
        const selectedIndex = currentLesson.sections.findIndex(section => section.name === currentSectionName);
        if (selectedIndex !== -1) {
          return selectedIndex;
        }
        return currentLesson.sections.findIndex(section => !completedSections[section.name]);
      })()
    : -1;

  const inputPlaceholder = connectionStatus !== 'connected'
    ? 'Connecting...'
    : dictation.isStarting
      ? 'Connecting to microphone'
      : dictation.isListening && !inputMessage
        ? 'Ready for dictation...'
        : 'Message TeachMeNow...';
  const effectiveSendOnEnter = !isPhoneInput && sendOnEnter;

  return (
    <div className={`chat-interface${showSidebar ? ' sidebar-open' : ''}`}>
      {showSidebar && currentLesson && currentLesson.sections && currentLesson.sections.length > 0 && (
        <div
          className="lesson-sidebar-backdrop"
          onClick={onHideSidebar}
          aria-hidden="true"
        />
      )}
      {showSidebar && currentLesson && currentLesson.sections && currentLesson.sections.length > 0 && (
        <div className="lesson-sidebar">
          <div className="lesson-sidebar-header">
            <h4>Lesson Contents</h4>
            <button
              type="button"
              className="lesson-sidebar-collapse-btn"
              onClick={onHideSidebar}
              title="Collapse sidebar"
            >
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 3L5 8l5 5" />
              </svg>
            </button>
          </div>
          <div className="lesson-sections">
            {currentLesson.sections.map((section, index) => {
              const completionStatus = completedSections[section.name];
              const isCurrent = index === currentSectionIndex;
              return (
                <div
                  key={index}
                  className={`lesson-section${completionStatus ? ' completed' : ''}${isCurrent ? ' current' : ''}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleSectionClick(section.name)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleSectionClick(section.name);
                    }
                  }}
                >
                  <h5>
                    {completionStatus ? <span className="section-status">✓</span> : null}
                    {isCurrent ? <span className="section-current">▶</span> : null}
                    {section.name}
                  </h5>
                  <p className="section-description">{section.description}</p>
                  {completionStatus ? (
                    <div className="section-status-label">{completionStatus}</div>
                  ) : isCurrent ? (
                    <div className="section-status-label current">CURRENT</div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div className="chat-main">
        <div className="messages-container">
          {messages.length === 0 ? (
            <div className="no-messages">
              <div className="empty-icon">👋</div>
              <h2>Welcome to TeachMeNow</h2>
              <p className="empty-subtitle">Select a lesson section to begin or ask me a question.</p>
            </div>
          ) : (
            uiMessages.map((message, idx) => {
              // Compute assistant index for this tutor message (0-based among tutor messages up to idx)
              const assistantIndex = uiMessages.slice(0, idx + 1).filter(m => m.type === 'tutor').length - 1;
              return (
              <div
                key={message.id}
                className={`message ${message.type}`}
                ref={(el) => {
                  messageRefs.current[message.id] = el;
                }}
              >
                <div className="message-content">
                  <div className="content">
                    {message.type === 'tutor' ? (
                      <div>{renderMathContent(message.content)}</div>
                    ) : message.type === 'user' ? (
                      message.content
                    ) : message.type === 'section_completion' ? (
                      <div className="completion-content">
                        <span className="completion-icon">✅</span>
                        {message.content}
                      </div>
                    ) : message.type === 'flashcards_unlocked' ? (
                      <div
                        className="completion-content flashcards-unlocked-content"
                        onClick={() => {
                          if (message.flashcards && message.flashcards.length > 0) {
                            toggleFlashcards(message.id);
                          }
                        }}
                        role={message.flashcards && message.flashcards.length > 0 ? 'button' : undefined}
                        tabIndex={message.flashcards && message.flashcards.length > 0 ? 0 : undefined}
                        onKeyDown={(event) => {
                          if ((event.key === 'Enter' || event.key === ' ') && message.flashcards && message.flashcards.length > 0) {
                            event.preventDefault();
                            toggleFlashcards(message.id);
                          }
                        }}
                      >
                        <span className="flashcards-unlocked-text">{message.content}</span>
                        {message.flashcards && message.flashcards.length > 0 && (
                          <button
                            type="button"
                            className="flashcards-popover-trigger"
                            onClick={(event) => {
                              event.stopPropagation();
                              toggleFlashcards(message.id);
                            }}
                            aria-expanded={openFlashcardMessageId === message.id}
                          >
                            View
                          </button>
                        )}
                        {openFlashcardMessageId === message.id && message.flashcards && message.flashcards.length > 0 && (
                          <div className="flashcards-popover" role="dialog" aria-label="Flashcards added to your deck">
                            {message.flashcards.map((card, cardIndex) => (
                              <div
                                key={`${message.id}-card-${cardIndex}`}
                                className="flashcards-popover-card"
                                onClick={(event) => event.stopPropagation()}
                              >
                                <div className="flashcards-popover-face flashcards-popover-face-top">
                                  {renderMathContent(card.top_text || '')}
                                </div>
                                <div className="flashcards-popover-face">
                                  {renderMathContent(card.bottom_text || '')}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : message.type === 'lesson_completion' ? (
                      <div
                        className="completion-content lesson-completion-trigger"
                        onClick={onLessonCompletionClick}
                        role={onLessonCompletionClick ? 'button' : undefined}
                        tabIndex={onLessonCompletionClick ? 0 : undefined}
                        onKeyDown={(event) => {
                          if (!onLessonCompletionClick) return;
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            onLessonCompletionClick();
                          }
                        }}
                      >
                        <span className="completion-icon">🎉</span>
                        {message.content}
                      </div>
                    ) : (
                      message.content
                    )}
                  </div>
                  {message.type !== 'section_completion' && message.type !== 'flashcards_unlocked' && message.type !== 'system' && (
                    <div className="timestamp">
                      {formatTimestamp(message.timestamp)}
                      {isAdmin && message.type === 'tutor' && message.modelContextAvailable ? (
                        <>
                          <button
                            className="export-context-btn"
                            style={{ marginLeft: 8 }}
                            title="Download context for this message"
                            onClick={() => handleExportContext(assistantIndex)}
                          >
                            ⬇️ Context
                          </button>
                          <button
                            className="export-context-btn"
                            style={{ marginLeft: 8 }}
                            title="Open this message's context in Prompt Tester"
                            onClick={() => handleOpenPromptTester(assistantIndex)}
                          >
                            🧪 Prompt Tester
                          </button>
                        </>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>
            );})
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="chat-controls">
          <form
            onSubmit={handleSubmit}
            className={`message-form${dictation.isActive ? ' dictation-active' : ''}`}
          >
            <div className="message-config" ref={configRef}>
              <button
                type="button"
                className="config-button"
                onClick={() => setShowConfig(prev => !prev)}
                aria-label="Message settings"
                aria-expanded={showConfig}
                title="Settings"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3"></circle>
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                </svg>
              </button>
              {showConfig && (
                <div className="config-popover" role="dialog" aria-label="Message settings">
                  <label className="config-option">
                    <input
                      type="checkbox"
                      checked={effectiveSendOnEnter}
                      disabled={isPhoneInput}
                      onChange={(event) => setSendOnEnter(event.target.checked)}
                    />
                    <span>Press Enter to send</span>
                  </label>
                  {isPhoneInput ? (
                    <div className="config-hint">On phones, Return inserts a new line. Use the send button to send.</div>
                  ) : !sendOnEnter && (
                    <div className="config-hint">Tip: Use Ctrl/Cmd+Enter to send.</div>
                  )}
                </div>
              )}
            </div>
            
            <textarea
              ref={inputRef}
              value={inputMessage}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder={inputPlaceholder}
              disabled={connectionStatus !== 'connected' || dictation.isActive}
              className="message-input"
              rows={1}
              style={{ resize: 'none' }}
            />
            
            <div className="send-actions">
              {dictation.isStarting ? (
                <div
                  className="dictation-loading-indicator"
                  role="status"
                  aria-label="Setting up dictation"
                >
                  <span className="dictation-loading-spinner" aria-hidden="true" />
                </div>
              ) : dictation.isActive ? (
                <>
                  <button
                    type="button"
                    className="send-button dictation-choice-button reject"
                    onClick={(event) => {
                      event.preventDefault();
                      dictation.stop();
                      setInputMessage('');
                    }}
                    aria-label="Reject transcription"
                    title="Reject transcription"
                  >
                    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path d="M18.3 5.71 12 12l6.3 6.29-1.41 1.42L10.59 13.41 4.29 19.71 2.88 18.3 9.17 12 2.88 5.7 4.29 4.29 10.59 10.59 16.89 4.29z"/>
                    </svg>
                  </button>
                  <button
                    type="button"
                    className="send-button dictation-choice-button accept"
                    onClick={(event) => {
                      event.preventDefault();
                      dictation.stop();
                    }}
                    aria-label="Accept transcription"
                    title="Accept transcription"
                  >
                    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                    </svg>
                  </button>
                </>
              ) : inputMessage.length === 0 ? (
                <button
                  type="button"
                  disabled={connectionStatus !== 'connected' || !dictation.isSupported}
                  className="send-button dictation-button"
                  onClick={(event) => {
                    event.preventDefault();
                    dictation.start();
                  }}
                  aria-label="Start dictation"
                  title={
                    !dictation.isSupported
                      ? 'Realtime dictation is not supported in this browser'
                      : 'Start dictation'
                  }
                >
                  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M12 14a3 3 0 0 0 3-3V5a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3zm5.3-3a5.3 5.3 0 0 1-10.6 0H5a7 7 0 0 0 6 6.92V21H8v2h8v-2h-3v-3.08A7 7 0 0 0 19 11h-1.7z"/>
                  </svg>
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={!inputMessage.trim() || connectionStatus !== 'connected'}
                  className="send-button"
                  title={effectiveSendOnEnter ? "Send message (Enter)" : "Send message"}
                >
                  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                  </svg>
                </button>
              )}
            </div>
            <span className="dictation-status" aria-live="polite">
              {dictation.isStarting
                ? 'Starting dictation'
                : dictation.isListening
                  ? 'Listening. Transcribed speech will appear in the message box.'
                  : ''}
            </span>
          </form>
          {dictation.error && (
            <div className="dictation-error" role="alert">
              {dictation.error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;
